/**
 * 国際化（i18n）サービス
 * 多言語対応のための翻訳管理
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';

// サポートする言語
export const SUPPORTED_LANGUAGES = {
  ja: '日本語',
  en: 'English',
  zh: '中文',
  ko: '한국어',
} as const;

export type Language = keyof typeof SUPPORTED_LANGUAGES;

// 翻訳データの型
export type TranslationData = {
  [key: string]: string | TranslationData;
};

// 翻訳ファイルをインポート
import jaTranslations from './locales/ja.json';
import enTranslations from './locales/en.json';
import zhTranslations from './locales/zh.json';
import koTranslations from './locales/ko.json';

// 翻訳データマップ
const translations: Record<Language, TranslationData> = {
  ja: jaTranslations,
  en: enTranslations,
  zh: zhTranslations,
  ko: koTranslations,
};

/**
 * i18nサービスクラス
 */
class I18nService {
  private currentLanguage: Language = 'ja';
  private listeners: Set<(lang: Language) => void> = new Set();

  constructor() {
    // ブラウザの言語設定から初期言語を決定
    const browserLang = navigator.language.split('-')[0];
    if (browserLang in SUPPORTED_LANGUAGES) {
      this.currentLanguage = browserLang as Language;
    }
    
    // LocalStorageから保存された言語を読み込み
    const savedLang = localStorage.getItem('language');
    if (savedLang && savedLang in SUPPORTED_LANGUAGES) {
      this.currentLanguage = savedLang as Language;
    }
  }

  /**
   * 現在の言語を取得
   */
  getLanguage(): Language {
    return this.currentLanguage;
  }

  /**
   * 言語を変更
   */
  setLanguage(lang: Language): void {
    if (!(lang in SUPPORTED_LANGUAGES)) {
      console.error(`Unsupported language: ${lang}`);
      return;
    }

    this.currentLanguage = lang;
    localStorage.setItem('language', lang);
    
    // HTMLのlang属性を更新
    document.documentElement.lang = lang;
    
    // リスナーに通知
    this.listeners.forEach(listener => listener(lang));
  }

  /**
   * 言語変更リスナーを登録
   */
  onLanguageChange(listener: (lang: Language) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * 翻訳を取得
   */
  translate(key: string, params?: Record<string, any>): string {
    const keys = key.split('.');
    let translation: any = translations[this.currentLanguage];

    for (const k of keys) {
      if (translation && typeof translation === 'object' && k in translation) {
        translation = translation[k];
      } else {
        console.warn(`Translation not found: ${key}`);
        return key;
      }
    }

    if (typeof translation !== 'string') {
      console.warn(`Translation is not a string: ${key}`);
      return key;
    }

    // パラメータの置換
    if (params) {
      return this.interpolate(translation, params);
    }

    return translation;
  }

  /**
   * 短縮形
   */
  t = this.translate.bind(this);

  /**
   * パラメータを翻訳文字列に埋め込む
   */
  private interpolate(str: string, params: Record<string, any>): string {
    return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key]?.toString() || match;
    });
  }

  /**
   * 数値フォーマット
   */
  formatNumber(value: number): string {
    return new Intl.NumberFormat(this.currentLanguage).format(value);
  }

  /**
   * 通貨フォーマット
   */
  formatCurrency(value: number, currency = 'JPY'): string {
    return new Intl.NumberFormat(this.currentLanguage, {
      style: 'currency',
      currency,
    }).format(value);
  }

  /**
   * 日付フォーマット
   */
  formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(this.currentLanguage, options).format(d);
  }

  /**
   * 相対時間フォーマット
   */
  formatRelativeTime(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diff = (d.getTime() - now.getTime()) / 1000; // 秒単位の差

    const rtf = new Intl.RelativeTimeFormat(this.currentLanguage, { numeric: 'auto' });

    if (Math.abs(diff) < 60) {
      return rtf.format(Math.round(diff), 'second');
    } else if (Math.abs(diff) < 3600) {
      return rtf.format(Math.round(diff / 60), 'minute');
    } else if (Math.abs(diff) < 86400) {
      return rtf.format(Math.round(diff / 3600), 'hour');
    } else if (Math.abs(diff) < 2592000) {
      return rtf.format(Math.round(diff / 86400), 'day');
    } else if (Math.abs(diff) < 31536000) {
      return rtf.format(Math.round(diff / 2592000), 'month');
    } else {
      return rtf.format(Math.round(diff / 31536000), 'year');
    }
  }

  /**
   * 複数形の処理
   */
  plural(count: number, key: string): string {
    const pluralRules = new Intl.PluralRules(this.currentLanguage);
    const rule = pluralRules.select(count);
    const pluralKey = `${key}.${rule}`;
    
    // 複数形のキーが存在するか確認
    const translation = this.translate(pluralKey);
    if (translation !== pluralKey) {
      return this.interpolate(translation, { count });
    }
    
    // フォールバック
    return this.interpolate(this.translate(key), { count });
  }
}

// シングルトンインスタンス
export const i18n = new I18nService();

/**
 * i18n Context
 */
interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
  formatNumber: (value: number) => string;
  formatCurrency: (value: number, currency?: string) => string;
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  formatRelativeTime: (date: Date | string) => string;
  plural: (count: number, key: string) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

/**
 * i18n Provider
 */
export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(i18n.getLanguage());

  useEffect(() => {
    const unsubscribe = i18n.onLanguageChange((lang) => {
      setLanguageState(lang);
    });
    return unsubscribe;
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    i18n.setLanguage(lang);
  }, []);

  const value: I18nContextValue = {
    language,
    setLanguage,
    t: i18n.t,
    formatNumber: i18n.formatNumber.bind(i18n),
    formatCurrency: i18n.formatCurrency.bind(i18n),
    formatDate: i18n.formatDate.bind(i18n),
    formatRelativeTime: i18n.formatRelativeTime.bind(i18n),
    plural: i18n.plural.bind(i18n),
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * useI18n Hook
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

/**
 * withI18n HOC
 */
export function withI18n<P extends object>(
  Component: React.ComponentType<P & I18nContextValue>
): React.FC<P> {
  return (props: P) => {
    const i18nProps = useI18n();
    return <Component {...props} {...i18nProps} />;
  };
}
